import React, { useState } from "react";
import {
  Alert,
  Box,
  Container,
  Paper,
  Tab,
  Tabs,
} from "@mui/material";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import { useAuth } from "@workos-inc/authkit-react";
import { ApiKeys, UsersManagement, WorkOsWidgets } from "@workos-inc/widgets";
import { useSnackbarPosition } from "../../../shared/hooks/useSnackbarPosition";
import { useAuthContext } from "../../../context/AuthContext";
import { usePageHeader } from "../../../context/LayoutContext";
import { usePermissions } from "../../../hooks/usePermissions";
import { canManageWidgetApiKeys, canManageWidgetUsersTable } from "../../../shared/auth/authorization";
import { useColorMode } from "../../../context/ColorModeContext";
import StationUsersPanel from "../../../shared/components/StationUsersPanel";

type ManagementTab = "classic" | "directory" | "api-keys";

const WIDGETS_API_HOSTNAME =
  import.meta.env.VITE_WORKOS_API_HOSTNAME || "api.workos.com";

const ManageUsersPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ManagementTab>("classic");

  const { mode } = useColorMode();
  const { organizationId } = useAuthContext();
  const { getAccessToken } = useAuth();
  const permissions = usePermissions();
  const hasWidgetUsersPermission = canManageWidgetUsersTable(permissions);
  const hasApiKeysPermission = canManageWidgetApiKeys(permissions);

  const snackbarPosition = useSnackbarPosition();

  usePageHeader({
    title: "Manage Users",
    subtitle: "Add, edit, and manage user accounts",
    icon: <PeopleAltIcon />,
  });

  return (
    <Container sx={{ pb: 4 }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: `1px solid`,
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            px: 3,
            pt: 2,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, value: ManagementTab) => setActiveTab(value)}
            sx={{
              minHeight: 48,
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.95rem",
                mr: 3,
                minHeight: 48,
              },
              "& .MuiTab-root:focus": { outline: "none" },
            }}
          >
            <Tab
              value="classic"
              icon={<PersonAddAlt1Icon fontSize="small" sx={{ mb: 0 }} />}
              iconPosition="start"
              label="Classic Tools"
            />
            <Tab
              value="directory"
              icon={<PeopleAltIcon fontSize="small" sx={{ mb: 0 }} />}
              iconPosition="start"
              label="Directory Widget"
            />
            <Tab
              value="api-keys"
              icon={<KeyOutlinedIcon fontSize="small" sx={{ mb: 0 }} />}
              iconPosition="start"
              label="API Keys"
            />
          </Tabs>
        </Box>

        <Box sx={{ p: 3 }}>
          {activeTab === "classic" && (
            <Box>
              <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                Use <strong>Classic Tools</strong> for internal metadata fields (rank, badge, assignments).
                Use the <strong>Directory Widget</strong> for core identity management.
              </Alert>
              <StationUsersPanel
                stationId={organizationId!}
                snackbarSxOverrides={snackbarPosition}
              />
            </Box>
          )}

          {activeTab === "directory" && (
            <Box sx={{ minHeight: 600 }}>
              {hasWidgetUsersPermission ? (
                <WorkOsWidgets apiHostname={WIDGETS_API_HOSTNAME} theme={{ appearance: mode === "dark" ? "dark" : "light" }}>
                  <UsersManagement authToken={getAccessToken} />
                </WorkOsWidgets>
              ) : (
                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                  Missing permission <code>widgets:users-table:manage</code>. Use Classic Tools or update
                  role permissions to enable the WorkOS Directory widget.
                </Alert>
              )}
            </Box>
          )}

          {activeTab === "api-keys" && (
            <Box sx={{ minHeight: 500 }}>
              {hasApiKeysPermission ? (
                <WorkOsWidgets apiHostname={WIDGETS_API_HOSTNAME} theme={{ appearance: mode === "dark" ? "dark" : "light" }}>
                  <ApiKeys authToken={getAccessToken} />
                </WorkOsWidgets>
              ) : (
                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                  Missing permission <code>widgets:api-keys:manage</code>. Contact an administrator
                  to enable API key management.
                </Alert>
              )}
            </Box>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default ManageUsersPage;
